import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Button,
  IconButton,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Alert,
  Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { DataGrid } from "@mui/x-data-grid";
import { useLocation, useNavigate } from "react-router-dom";

import Header from "../../components/Header";
import { authGet, authPost, unwrapRows } from "../../lib/authApi";

// Helpers
const toLocalDate = (v) => (v ? new Date(v) : null);
const fmtDOB = (v) => {
  const d = toLocalDate(v);
  return d ? d.toLocaleDateString() : "—";
};

function pickRows(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.rows)) return resp.rows;
  if (Array.isArray(resp.data)) return resp.data;
  return unwrapRows(resp) || [];
}

function AddPatientDialog({ open, onClose, onCreated, orgId }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    dob: "",
    gender: "",
    mrn: "",
    patient_external_id: "",
  });

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setError("");
    setForm({
      first_name: "",
      last_name: "",
      dob: "",
      gender: "",
      mrn: "",
      patient_external_id: "",
    });
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    setError("");

    if (!orgId) {
      setError("No organization selected.");
      return;
    }

    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError("First name and last name are required.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        org_id: orgId,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        dob: form.dob || null,
        gender: form.gender || null,
        mrn: form.mrn.trim() || null,
        patient_external_id: form.patient_external_id.trim() || null,
      };

      const resp = await authPost("/api/patients", payload);
      onCreated?.(resp?.patient || resp);
      onClose();
    } catch (e) {
      setError(e.message || "Failed to create patient.");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Add Patient
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 12, top: 10 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.25 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="First Name *"
              fullWidth
              value={form.first_name}
              onChange={set("first_name")}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Last Name *"
              fullWidth
              value={form.last_name}
              onChange={set("last_name")}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="DOB"
              type="date"
              fullWidth
              value={form.dob}
              onChange={set("dob")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Gender"
              fullWidth
              value={form.gender}
              onChange={set("gender")}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
              <MenuItem value="unknown">Unknown</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="MRN"
              fullWidth
              value={form.mrn}
              onChange={set("mrn")}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Patient ID (External)"
              fullWidth
              value={form.patient_external_id}
              onChange={set("patient_external_id")}
              helperText="Leave blank and the server will generate one."
            />
          </Grid>

          {!!error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button startIcon={<SaveIcon />} variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Patients() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { search } = useLocation();
  const qs = useMemo(() => new URLSearchParams(search), [search]);

  const [toast, setToast] = useState({ open: false, msg: "", severity: "error" });

  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgId, setOrgId] = useState(qs.get("org") || "");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openAdd, setOpenAdd] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [genderFilter, setGenderFilter] = useState("all");

  const syncToUrl = (nextOrg) => {
    const url = new URL(window.location.href);
    if (nextOrg) url.searchParams.set("org", nextOrg);
    else url.searchParams.delete("org");
    navigate(`${url.pathname}?${url.searchParams.toString()}`, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;

    async function loadOrgs() {
      setOrgsLoading(true);
      try {
        const j = await authGet("/api/organizations");
        const list = j?.organizations || j?.rows || pickRows(j) || [];

        if (cancelled) return;
        setOrgs(list);

        if (!orgId) {
          const urlOrg = qs.get("org");
          const first = list?.[0]?.id;
          setOrgId(urlOrg || first || "");
        }
      } catch (e) {
        console.error("Load orgs failed:", e);
        if (!cancelled) {
          setOrgs([]);
          setToast({
            open: true,
            msg: `Failed to load organizations: ${e.message}`,
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    }

    loadOrgs();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRows = async () => {
    if (!orgId) {
      setRows([]);
      return;
    }

    setLoading(true);
    try {
      const j = await authGet(`/api/patients?org_id=${encodeURIComponent(orgId)}`);
      const patients = j?.patients || pickRows(j);

      const mapped = patients.map((p) => ({
        id: p.id,
        org_id: p.org_id,
        first_name: p.first_name || "",
        last_name: p.last_name || "",
        full_name: [p.first_name, p.last_name].filter(Boolean).join(" ") || "—",
        dob: p.dob || null,
        gender: p.gender || "",
        mrn: p.mrn || "",
        patient_external_id: p.patient_external_id || "",
        created_at: p.created_at || null,
        updated_at: p.updated_at || null,
      }));

      setRows(mapped);
    } catch (e) {
      console.error("Failed to load patients:", e);
      setRows([]);
      setToast({
        open: true,
        msg: `Failed to load patients: ${e.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!orgId) return;
    syncToUrl(orgId);
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const genders = useMemo(() => {
    const set = new Set(rows.map((r) => String(r.gender || "").toLowerCase()).filter(Boolean));
    return ["all", ...Array.from(set).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();

    return rows.filter((r) => {
      if (genderFilter !== "all" && String(r.gender || "").toLowerCase() !== genderFilter) {
        return false;
      }

      if (!q) return true;

      return `${r.patient_external_id} ${r.first_name} ${r.last_name} ${r.full_name} ${r.gender} ${r.mrn}`
        .toLowerCase()
        .includes(q);
    });
  }, [rows, searchText, genderFilter]);

  const columns = [
    {
      field: "menu",
      headerName: "",
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: () => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Tooltip title="Row menu">
            <IconButton size="small" sx={{ zIndex: 2 }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    { field: "patient_external_id", headerName: "Patient ID", width: 170 },
    { field: "last_name", headerName: "Last Name", flex: 1, minWidth: 150 },
    { field: "first_name", headerName: "First Name", flex: 1, minWidth: 150 },
    {
      field: "dob",
      headerName: "DOB",
      width: 140,
      renderCell: ({ row }) => fmtDOB(row.dob),
    },
    {
      field: "gender",
      headerName: "Gender",
      width: 120,
      renderCell: ({ row }) => row.gender || "—",
    },
    {
      field: "mrn",
      headerName: "MRN",
      width: 150,
      renderCell: ({ row }) => row.mrn || "—",
    },
    {
      field: "created_at",
      headerName: "Created",
      width: 190,
      renderCell: ({ row }) => (row.created_at ? new Date(row.created_at).toLocaleString() : "—"),
    },
  ];

  return (
    <Box p={3} sx={{ pb: 6 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Header title="Patients" subtitle="Manage patient records" />

        <Box display="flex" gap={1} alignItems="center">
          <TextField
            select
            size="small"
            label="Organization"
            value={orgId || ""}
            onChange={(e) => setOrgId(e.target.value)}
            sx={{ minWidth: 220 }}
            disabled={orgsLoading || !orgs.length}
          >
            {orgs.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 260 }}
          />

          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchRows} disabled={!orgId}>
            Refresh
          </Button>

          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAdd(true)} disabled={!orgId}>
            New
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Gender"
                fullWidth
                size="small"
                value={genderFilter}
                onChange={(e) => setGenderFilter(e.target.value)}
              >
                {genders.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g === "all" ? "All" : g}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ height: 640, width: "100%" }}>
            <DataGrid
              rows={filtered}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              pageSizeOptions={[15, 25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 15, page: 0 } },
                sorting: { sortModel: [{ field: "last_name", sort: "asc" }] },
              }}
              sx={{ "& .MuiDataGrid-columnHeaders": { background: theme.palette.background.paper } }}
            />
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <Typography variant="caption" color="text.secondary">
              {filtered.length} patient{filtered.length === 1 ? "" : "s"}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <AddPatientDialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        onCreated={() => {
          fetchRows();
          setToast({ open: true, msg: "Patient created.", severity: "success" });
        }}
        orgId={orgId}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity || "info"}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}